import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { MouseEvent } from 'react'
import { agentReleasesQuery } from '../api/settingsAgentReleases'

/*
 * AGENT RELEASES, iconita lui in bara - `header.php`, blocul cu acelasi nume.
 *
 * A stat in Settings, ca o intrare printre alte treizeci - dar nu e o setare:
 * de acolo pleaca binarul care ruleaza pe masinile Unix ale tuturor firmelor,
 * si zona are sa creasca. Scos la vedere, in bara de sus, se ajunge la el
 * dintr-o apasare, din orice ecran.
 *
 * Se scrie NUMAI cand API-ul spune `canPublishAgents` (cutiile postale din
 * `can_manage_unix_agent_releases()`, prin `Tiers::isGlobalAdmin`). Pentru
 * ceilalti nu e ascuns cu CSS: `Shell.tsx` nu il monteaza deloc.
 *
 * CELE TREI COLTURI ALE SEMNULUI
 * ---------------------------------------------------------------------------
 *   jos-dreapta, verde   cate masini sunt pe versiunea publicata
 *   sus-stanga, galben   cate NU sunt la zi, dar au primit ceva in ultima zi
 *   sus-dreapta, rosu    cate nu sunt la zi si n-au mai primit nimic de o zi
 *
 * Sunt exact cifrele de pe ecranul de Agent Releases, din acelasi raspuns
 * (`AgentReleases::page` -> `UnixFleet::countsFrom`): doua socoteli scrise
 * separat ar fi ajuns sa dea doua raspunsuri la aceeasi intrebare. Portalul
 * le tine cinci minute in sesiune (`unix_agent_fleet_counts`); aici acelasi
 * ragaz, prin `staleTime`. Un colt cu zero nu se scrie. Cand `published` e
 * false (catalogul n-a raspuns) nu se aprinde niciunul: numerele n-ar insemna
 * nimic fata de o versiune necunoscuta.
 *
 * Stilul bulinelor (`.od-ar-pips`, `.od-ar-pip`) e in `public/css/od_ux.css`,
 * foaie incarcata pe fiecare pagina (PORTAL_STYLES din `vite.config.ts`).
 */

const FIVE_MINUTES = 5 * 60 * 1000

/** Numarul asa cum incape intr-o bulina: peste 99 cifrele nu mai spun nimic. */
function pip(n: number): string {
  return n > 99 ? '99+' : String(n)
}

export function AgentReleasesMark() {
  const navigate = useNavigate()
  const fleet = useQuery({ ...agentReleasesQuery(), staleTime: FIVE_MINUTES })

  const counts = fleet.data?.counts
  const live = counts?.published === true
  const current = live ? counts.current : 0
  const late = live ? counts.late : 0
  const stuck = live ? counts.stuck : 0
  const silent = counts?.silent ?? 0

  /* Lamurirea de la trecerea cu mausul: culorile spun "bine / atentie / rau",
     dar nu spun DESPRE CE. Fiecare numar isi scrie aici intelesul intreg. */
  const title = ['Agent Releases']
  if (current > 0) title.push(`${current} on the published version`)
  if (late > 0) title.push(`${late} still catching up`)
  if (stuck > 0) title.push(`${stuck} stuck - nothing for over a day`)
  if (silent > 0) title.push(`${silent} silent for over three days`)

  const open = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    void navigate({ to: '/settings/agent_releases' })
  }

  return (
    <li className="nav-quick-actions" data-testid="agent-releases-mark">
      {/* Ancora e flex, ca semnul sa stea la mijlocul barii, ca glifele de fonta de langa el. */}
      <a href="/app/settings/agent_releases" title={title.join(' — ')} style={{ display: 'flex', alignItems: 'center' }} onClick={open}>
        {/* Invelisul are exact marimea desenului: bulinele se aseaza dupa colturile LUI, nu ale ancorei. */}
        <span className="od-ar-pips">
          <AgentReleasesIcon size={28} />
          {late > 0 && <span className="od-ar-pip is-late">{pip(late)}</span>}
          {stuck > 0 && <span className="od-ar-pip is-stuck">{pip(stuck)}</span>}
          {current > 0 && <span className="od-ar-pip is-current">{pip(current)}</span>}
        </span>
      </a>
    </li>
  )
}

/*
 * Semnul de la Agent Releases - `_partials/icons/agent_releases.php`: hexagonul
 * taiat in trei, cate un sistem in fiecare romb - Windows, macOS, Linux.
 *
 * Scris in pagina, nu legat ca poza, ca sa ia culoarea din jur prin
 * `currentColor` - alb in bara, inchis oriunde va mai fi pus. Monocrom
 * dinadins: in bara, culorile marcii faceau o pata langa sase semne albe.
 * Burta pinguinului si ochii lui sunt gauri (`evenodd`), nu alt strat.
 */
export function AgentReleasesIcon({ size = 28 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width={size} height={size} fill="currentColor" aria-hidden="true" style={{ verticalAlign: 'middle' }}>
      {/* Hexagonul si cele doua taieturi care il impart in trei romburi. */}
      <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round">
        <path d="M17.5 6.9 L46.5 6.9 L61 32 L46.5 57.1 L17.5 57.1 L3 32 Z" />
        <path d="M32 32 L17.5 6.9 M32 32 L61 32 M32 32 L17.5 57.1" />
      </g>
      {/* WINDOWS - patru ochiuri de geam, in rombul de sus. */}
      <g transform="translate(39.6 19.6)">
        <rect x="-6.4" y="-5.8" width="5.8" height="5.2" rx="0.6" />
        <rect x="0.6" y="-6.4" width="5.8" height="5.8" rx="0.6" />
        <rect x="-6.4" y="0.6" width="5.8" height="5.8" rx="0.6" />
        <rect x="0.6" y="0.6" width="5.8" height="6.4" rx="0.6" />
      </g>
      {/* macOS - marul, plin: conturul se pierde la marimea asta, silueta nu. */}
      <g transform="translate(38.4 45) scale(1.2) translate(-36.7 -31)">
        <path d="M35.6 27.9c-1.6 0-2.9 1-3.6 2.4-.8 1.6-.5 4.1.5 6 .5.9 1.1 2 2 2 .8 0 1.1-.5 2.1-.5s1.3.5 2.1.5c.9 0 1.5-1 2-1.9.3-.6.5-1.1.7-1.7-1.8-.7-2.6-2.5-2.1-4.2.3-1 1-1.7 1.8-2.1-.7-.9-1.8-1.4-2.9-1.4-.9 0-1.5.4-2 .4-.5 0-1.1-.4-2-.4z" />
        <path d="M38.6 24c1 .1 2 .7 2.5 1.6-.9.5-1.6 1.3-1.7 2.3-1-.1-1.9-.7-2.3-1.6.4-1 1-1.9 1.5-2.3z" />
      </g>
      {/* LINUX - Tux, cu burta si ochii scobiti prin `evenodd`. */}
      <g transform="translate(17.4 31.8) scale(1.2) translate(-50.6 -32)">
        <path
          fillRule="evenodd"
          d="M50.6 24.6c-2.4 0-4 1.9-4 4.3 0 .9.2 1.5.1 2.1-.2 1.3-1.5 2.9-1.5 4.7 0 1.9 1.9 3 5.4 3s5.4-1.1 5.4-3c0-1.8-1.3-3.4-1.5-4.7-.1-.6.1-1.2.1-2.1 0-2.4-1.6-4.3-4-4.3z M50.6 31.5a2.7 3.1 0 1 0 0 6.2 2.7 3.1 0 1 0 0-6.2z M49.4 25.9a.95.95 0 1 0 0 1.9.95.95 0 1 0 0-1.9z M51.8 25.9a.95.95 0 1 0 0 1.9.95.95 0 1 0 0-1.9z"
        />
        <ellipse cx="48.3" cy="39.2" rx="2" ry="1" />
        <ellipse cx="52.9" cy="39.2" rx="2" ry="1" />
      </g>
    </svg>
  )
}
